import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  CircularProgress,
  IconButton
} from '@mui/material';
import {
  Close as CloseIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { makeApiCall } from '../../config/api';

const TimeEntryDialog = ({ 
  open, 
  onClose, 
  entry = null, 
  employees = [], 
  onSuccess 
}) => {
  const isEdit = Boolean(entry);
  
  // Form state
  const [formData, setFormData] = useState({
    user_id: '',
    clock_in_time: '',
    clock_out_time: '',
    notes: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Initialize form data
  useEffect(() => {
    if (entry) {
      // Edit mode - populate with existing data
      const clockInDate = new Date(entry.clock_in_time);
      const clockOutDate = entry.clock_out_time ? new Date(entry.clock_out_time) : null;
      
      setFormData({
        user_id: entry.user_id,
        clock_in_time: formatDateTimeLocal(clockInDate),
        clock_out_time: clockOutDate ? formatDateTimeLocal(clockOutDate) : '',
        notes: entry.notes || ''
      });
    } else {
      // Create mode - reset form
      const now = new Date();
      setFormData({
        user_id: '',
        clock_in_time: formatDateTimeLocal(now),
        clock_out_time: '',
        notes: ''
      });
    }
    setError('');
  }, [entry, open]);

  // Format date for datetime-local input
  const formatDateTimeLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(''); // Clear error when user makes changes
  };

  // Validate form
  const validateForm = () => {
    if (!formData.user_id) {
      setError('Please select an employee');
      return false;
    }
    
    if (!formData.clock_in_time) {
      setError('Clock in time is required');
      return false;
    }
    
    // If both times provided, validate clock out is after clock in
    if (formData.clock_out_time && formData.clock_in_time) {
      const clockIn = new Date(formData.clock_in_time);
      const clockOut = new Date(formData.clock_out_time);
      
      if (clockOut <= clockIn) {
        setError('Clock out time must be after clock in time');
        return false;
      }
      
      // Check if the duration is reasonable (not more than 336 hours / 2 weeks)
      const diffHours = (clockOut - clockIn) / (1000 * 60 * 60);
      if (diffHours > 336) {
        setError('Time entry cannot exceed 336 hours (2 weeks)');
        return false;
      }
      
      // Warn for unusually long sessions (over 24 hours)
      if (diffHours > 24) {
        console.warn(`Long time entry: ${diffHours.toFixed(2)} hours`);
      }
    }
    
    return true;
  };

  // Calculate hours worked for preview
  const calculateHours = () => {
    if (!formData.clock_in_time || !formData.clock_out_time) return null;
    
    const clockIn = new Date(formData.clock_in_time);
    const clockOut = new Date(formData.clock_out_time);
    const diffHours = (clockOut - clockIn) / (1000 * 60 * 60);
    
    return diffHours > 0 ? diffHours.toFixed(2) : null;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      setError('');
      
      // datetime-local values have no zone. Send them as UTC instants, or the
      // UTC database reads "05:25" as 05:25 UTC and it shows as 1:25 AM here.
      const toInstant = (value) => (value ? new Date(value).toISOString() : null);
      const submitData = {
        user_id: parseInt(formData.user_id),
        clock_in_time: toInstant(formData.clock_in_time),
        clock_out_time: toInstant(formData.clock_out_time),
        notes: formData.notes.trim() || null
      };
      
      let response;
      if (isEdit) {
        // Update existing entry
        response = await makeApiCall(`api/time-tracking/admin/entries/${entry.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(submitData)
        });
      } else {
        // Create new entry
        response = await makeApiCall('api/time-tracking/admin/entries', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(submitData)
        });
      }
      
      if (response.ok) {
        const data = await response.json();
        onSuccess(data.message);
        handleClose();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save time entry');
      }
      
    } catch (error) {
      console.error('Error saving time entry:', error);
      setError('Failed to save time entry');
    } finally {
      setLoading(false);
    }
  };

  // Handle dialog close
  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  // Get selected employee info
  const selectedEmployee = employees.find(emp => emp.user_id === parseInt(formData.user_id));
  const calculatedHours = calculateHours();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          m: { xs: 1, sm: 2 },
          maxHeight: { xs: '95vh', sm: '90vh' }
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccessTimeIcon />
            {isEdit ? 'Edit Time Entry' : 'Create New Time Entry'}
          </Typography>
          <IconButton onClick={handleClose} disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Employee Selection */}
            <Grid size={12}>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon fontSize="small" />
                  Employee Information
                </Typography>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth required disabled={isEdit}>
                <InputLabel>Employee</InputLabel>
                <Select
                  value={formData.user_id}
                  label="Employee"
                  onChange={(e) => handleChange('user_id', e.target.value)}
                >
                  <MenuItem value="">Select an employee</MenuItem>
                  {employees.map((emp) => (
                    <MenuItem key={emp.user_id} value={emp.user_id}>
                      {emp.employee_name} (@{emp.username})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {isEdit && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Employee cannot be changed when editing
                </Typography>
              )}
            </Grid>

            {selectedEmployee && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box sx={{ pt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Selected:</strong> {selectedEmployee.employee_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Username:</strong> @{selectedEmployee.username}
                  </Typography>
                </Box>
              </Grid>
            )}

            {/* Time Information */}
            <Grid size={12}>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccessTimeIcon fontSize="small" />
                  Time Information
                </Typography>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                required
                label="Clock In Time"
                type="datetime-local"
                value={formData.clock_in_time}
                onChange={(e) => handleChange('clock_in_time', e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="When the employee started working"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Clock Out Time"
                type="datetime-local"
                value={formData.clock_out_time}
                onChange={(e) => handleChange('clock_out_time', e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Leave empty if still working"
              />
            </Grid>

            {/* Hours Calculation Preview */}
            {calculatedHours && (
              <Grid size={12}>
                <Box sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 1, border: 1, borderColor: 'primary.200' }}>
                  <Typography variant="subtitle2" color="primary.main" gutterBottom>
                    Calculated Hours: {calculatedHours}h
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    This will be automatically calculated and saved
                  </Typography>
                </Box>
              </Grid>
            )}

            {/* Notes */}
            <Grid size={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="Add any relevant notes about this time entry..."
                helperText="Optional notes for this time entry"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={handleClose} 
            disabled={loading}
            variant="outlined"
            size="large"
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading}
            size="large"
            sx={{ minWidth: 120 }}
          >
            {loading ? (
              <CircularProgress size={20} />
            ) : (
              isEdit ? 'Update Entry' : 'Create Entry'
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default TimeEntryDialog;
